import LandingSection from "./homePage/LandingSection";
import { AnimatedDeviceTransitions, AnimatedDeviceTypes } from "../components/AnimatedDevice";
import ShowcaseSection from "./homePage/ShowcaseSection";
import AnimatedText from "../components/AnimatedText";
import { useRef } from "react";
import { animatedTextFadeInNoSplit, animatedTextParagraphFadeIn, animatedTextZoomInWithColor } from "../components/animatedTextPresets";
import FormSection from "./homePage/FormSection";
import Nav from "./homePage/Nav";
import { LINKS } from "../specs";

const HomePage = () => {
    const aboutSectionRef = useRef<HTMLSelectElement>(null);

    const color = '#222';
    const accentHlColor = '#0265DC';
    const colorDark = '#E6E6E6';
    const brandingHlColor = '#FFBB63';
    const designHlColor = '#BD8BFC';
    const engineeringHlColor = '#59A7F6';

    return <div className="hp select-none">

        <Nav />

        <LandingSection />

        <section ref={aboutSectionRef} className="hp-sec-about fx-column ai-center" id="about">
            <AnimatedText className="fs-8 fw-700 px-text" {...animatedTextZoomInWithColor(color, accentHlColor)}>λ</AnimatedText>
            <AnimatedText className="ff-title fs-3 px-text" {...animatedTextZoomInWithColor(color, accentHlColor)}>aframe.studio</AnimatedText>
            <AnimatedText className="fs-1p5 ta-c mt-2 lh-p" {...animatedTextParagraphFadeIn}>
                We're a team of dream-chasers and problem-solving aficionados of the digital world. Together, we’ll make the impossible happen for the ambitious minds that dare to dream big
            </AnimatedText>
            <AnimatedText className="fs-1p5 ta-c mt-2 lh-p" {...animatedTextParagraphFadeIn}>
                We're on a mission to bridge the gap between business and the digital realm. If you're a company that knows there's more untapped potential in your digital presence, but you're not quite sure how to unlock it, we're here to help. Let's make your digital work smarter for you
            </AnimatedText>
        </section>

        <section className="hp-sec-services fx jc-center px-text" id="services">
            <AnimatedText
                className="ff-alt fs-4 ta-c"
                itemClassName="text-gradient"
                {...animatedTextFadeInNoSplit}>
                What we do best
            </AnimatedText>
        </section>

        <ShowcaseSection
            className="--brand"
            heading="Branding"
            color={colorDark}
            hlColor={brandingHlColor}
            description="Sure, every brand is unique. The question is do your customers realise it? We’ll make sure they do"
            canvasBgSrc="./assets/branding-bg.png"
            canvasSize={[58, 32]}
            canvasSizeSmall={[58, 58]}
            devices={[
                {
                    pos: [264, 0],
                    posSmall: [260, 100],
                    scale: .4,
                    scaleSmall: .6,
                    elevation: 2,
                    animProps: {
                        type: AnimatedDeviceTypes.Tablet,
                        screens: [
                            { src: './assets/branding-tb-1.png', transition: AnimatedDeviceTransitions.AppSwitch },
                            { src: './assets/branding-tb-2.png', transition: AnimatedDeviceTransitions.AppSwitch },
                            { src: './assets/branding-tb-3.png', transition: AnimatedDeviceTransitions.AppSwitch },
                        ]
                    }
                },
                {
                    pos: [104, 72],
                    posSmall: [100, 282],
                    scale: .5,
                    scaleSmall: .75,
                    elevation: 3,
                    animProps: {
                        type: AnimatedDeviceTypes.Phone,
                        screens: [
                            { src: './assets/branding-ph-1.png', transition: AnimatedDeviceTransitions.AppSwitch },
                            { src: './assets/branding-ph-2.png', transition: AnimatedDeviceTransitions.AppSwitch },
                            { src: './assets/branding-ph-3.png', transition: AnimatedDeviceTransitions.AppSwitch },
                        ]
                    }
                },
            ]}
        />

        <ShowcaseSection
            className="--design"
            heading="Designing"
            color={colorDark}
            hlColor={designHlColor}
            description="Experiences that move people. Function and form. Hearts and minds. We don’t think these are mutually exclusive and that’s what good design means to us"
            canvasBgSrc="./assets/design-bg.png"
            canvasSize={[58, 32]}
            canvasSizeSmall={[58, 62]}
            devices={[
                {
                    pos: [507, 194],
                    posSmall: [480, 514],
                    scale: .3,
                    scaleSmall: .45,
                    elevation: 1,
                    animProps: {
                        type: AnimatedDeviceTypes.Tablet,
                        screens: [
                            { src: './assets/design-tb2-1.png', transition: AnimatedDeviceTransitions.SlideInBottom },
                            { src: './assets/design-tb2-2.png', transition: AnimatedDeviceTransitions.SlideInLeft },
                            { src: './assets/design-tb2-3.png', transition: AnimatedDeviceTransitions.SlideInRight },
                        ]
                    }
                },
                {
                    pos: [130, 0],
                    posSmall: [200, 0],
                    scale: .4,
                    scaleSmall: .6,
                    elevation: 2,
                    animProps: {
                        type: AnimatedDeviceTypes.Tablet,
                        screens: [
                            { src: './assets/design-tb1-1.png', transition: AnimatedDeviceTransitions.AppSwitch },
                            { src: './assets/design-tb1-2.png', transition: AnimatedDeviceTransitions.SlideInRight },
                            { src: './assets/design-tb1-3.png', transition: AnimatedDeviceTransitions.SlideInRight },
                            { src: './assets/design-tb1-4.png', transition: AnimatedDeviceTransitions.SlideInRight },
                        ]
                    }
                },
                {
                    pos: [0, 72],
                    posSmall: [0, 190],
                    scale: .5,
                    scaleSmall: .75,
                    elevation: 3,
                    animProps: {
                        type: AnimatedDeviceTypes.Phone,
                        screens: [
                            { src: './assets/design-ph-1.png', transition: AnimatedDeviceTransitions.AppSwitch },
                            { src: './assets/design-ph-2.png', transition: AnimatedDeviceTransitions.FadeInScrollOut },
                            { src: './assets/design-ph-3.png', transition: AnimatedDeviceTransitions.SlideInRight },
                            { src: './assets/design-ph-4.png', transition: AnimatedDeviceTransitions.AppSwitch },
                            { src: './assets/design-ph-5.png', transition: AnimatedDeviceTransitions.FadeIn },
                        ]
                    }
                },
            ]}
        />

        <ShowcaseSection
            className="--engg"
            heading="Engineering"
            color={colorDark}
            hlColor={engineeringHlColor}
            description="Got an awesome idea? You’re halfway there! Now let us make a fully functioning product out of it"
            canvasBgSrc="./assets/engg-bg.png"
            isLaptop
            canvasSize={[58, 32]}
            canvasSizeSmall={[58, 48]}
            devices={[{
                pos: [54, 42],
                posSmall: [0, 32],
                scale: .5,
                scaleSmall: .75,
                animProps: {
                    type: AnimatedDeviceTypes.Laptop,
                    screens: [
                        { src: './assets/engg-laptop-1.png', transition: AnimatedDeviceTransitions.AppSwitch },
                        { src: './assets/engg-laptop-2.png', transition: AnimatedDeviceTransitions.SlideInRight },
                        { src: './assets/engg-laptop-3.png', transition: AnimatedDeviceTransitions.FadeIn },
                        { src: './assets/engg-laptop-4.png', transition: AnimatedDeviceTransitions.FadeIn },
                        { src: './assets/engg-laptop-5.png', transition: AnimatedDeviceTransitions.FadeIn },
                        { src: './assets/engg-laptop-6.png', transition: AnimatedDeviceTransitions.FadeIn },
                    ]
                }
            }]}
        />

        <section className="hp-sec-culture fx-column ai-center" id="culture">
            <AnimatedText className="fs-8 fw-700 px-text" {...animatedTextZoomInWithColor(color, accentHlColor)}>∞</AnimatedText>
            <AnimatedText className="ff-title fs-3 px-text" {...animatedTextZoomInWithColor(color, accentHlColor)}>Culture</AnimatedText>
            <AnimatedText className="fs-1p5 ta-c mt-2 lh-p" {...animatedTextParagraphFadeIn}>
                We believe that the best results come from working together over the long haul, and that's exactly what we offer - Digital Partnerships. We're all in this together, chasing the same goals and sharing the same vision
            </AnimatedText>
            <AnimatedText className="fs-1p5 ta-c mt-2 lh-p" {...animatedTextParagraphFadeIn}>
                Our complete suite of Branding, Design and Engineering services all under one roof is as bespoke as the brands we partner with. That means we come up with unique solutions for your unique problems. No one-size-fits-all solutions here. And we never skimp on creativity or performance
            </AnimatedText>
        </section>

        <FormSection />

        <section className="hp-sec-footer">
            <div className="fs-1p5 ff-title">aframe.studio</div>
            <div className="__columns fx mt-3">
                <div className="fx-column fx-grow">
                    <a href={LINKS.email}>connect@aframe.studio</a>
                    <p className="mt-1 fs-sm text-tertiary">Melbourne, Australia</p>
                </div>
                <div className="fx-column fx-grow ai-end ta-r">
                    <div className="__links">
                        <a href={LINKS.dribble} target="_blank">Dribble</a>
                        <a href={LINKS.instagram} target="_blank">Instagram</a>
                        <a href={LINKS.linkedIn} target="_blank">LinkedIn</a>
                    </div>
                    <p className="mt-1 fs-sm text-tertiary">© {new Date().getFullYear()} aframe.studio | All Rights Reserved</p>
                </div>
            </div>
        </section>
    </div >
}

export default HomePage;